import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, query, onSnapshot, doc, getDoc, setDoc, deleteDoc, serverTimestamp, getDocs, writeBatch, orderBy, updateDoc, addDoc, Timestamp, limit, where } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Modal from 'react-modal';

const STAGES = [
    { id: 'primer_contacto', label: 'Contacto' },
    { id: 'reunion_propuesta', label: 'Reunión' },
    { id: 'presupuesto_enviado', label: 'Presupuesto' },
    { id: 'presupuesto_aprobado', label: 'Aprobado' }
];

const PipelineTracker = ({ currentStatus, onStatusChange, editable = false }) => {
    const currentIndex = STAGES.findIndex(stage => stage.id === currentStatus);
    return (
        <div className="flex items-center justify-between w-full">
            {STAGES.map((stage, index) => {
                const isCompleted = index < currentIndex;
                const isCurrent = index === currentIndex;
                let bgColor = 'bg-gray-300';
                if (isCompleted) bgColor = 'bg-green-500';
                if (isCurrent) bgColor = 'bg-blue-500 animate-pulse';
                const node = (
                    <div className="flex flex-col items-center text-center px-1">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs ${bgColor} ${editable ? 'cursor-pointer' : ''}`}>
                            {index + 1}
                        </div>
                        <p className={`mt-1 text-xs font-semibold ${isCurrent ? 'text-blue-600' : 'text-gray-600'}`}>
                            {stage.label}
                        </p>
                    </div>
                );
                return (
                    <React.Fragment key={stage.id}>
                        {editable ? <button type="button" onClick={() => onStatusChange(stage.id)}>{node}</button> : node}
                        {index < STAGES.length - 1 && <div className="flex-1 h-1 bg-gray-300 rounded-full mx-1"></div>}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const getAlertaStatus = (lastInteractionDate) => {
    if (!lastInteractionDate) return 'sin_datos';
    const hoy = new Date();
    const fechaNota = lastInteractionDate.toDate();
    const diffTime = Math.abs(hoy - fechaNota);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return 'ok';
    if (diffDays <= 14) return 'alerta';
    return 'urgente';
};

const AlertaIndicator = ({ status }) => {
    const config = {
        ok: { color: 'bg-green-500', tooltip: 'Contactado hace ≤ 7 días' },
        alerta: { color: 'bg-yellow-500', tooltip: 'Contacto hace 8-14 días' },
        urgente: { color: 'bg-red-500', tooltip: 'Contacto hace ≥ 15 días' },
        sin_datos: { color: 'bg-gray-400', tooltip: 'Sin interacciones' }
    };
    const current = config[status] || config.sin_datos;
    return (
        <div className="relative group">
            <div className={`w-4 h-4 rounded-full ${current.color}`}></div>
            <div className="absolute bottom-full mb-2 hidden group-hover:block w-max bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
                {current.tooltip}
            </div>
        </div>
    );
};

const BitacoraModal = ({ seguimiento, onClose, coleccionOrigen }) => {
    const [bitacora, setBitacora] = useState([]);
    const [cargandoBitacora, setCargandoBitacora] = useState(true);
    const [nuevaNota, setNuevaNota] = useState('');

    useEffect(() => {
        const bitacoraRef = collection(db, 'clientes', seguimiento.clienteId, 'bitacora_seguimiento');
        const q = query(bitacoraRef, orderBy('fecha', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBitacora(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setCargandoBitacora(false);
        });
        return () => unsubscribe();
    }, [seguimiento.clienteId]);

    const handleAgregarNota = async () => {
        if (nuevaNota.trim() === '') return toast.error('La nota no puede estar vacía.');
        
        const bitacoraRef = collection(db, 'clientes', seguimiento.clienteId, 'bitacora_seguimiento');
        const seguimientoRef = doc(db, coleccionOrigen, seguimiento.id);
        const fechaActual = Timestamp.now();

        try {
            await addDoc(bitacoraRef, { texto: nuevaNota, fecha: fechaActual });
            await updateDoc(seguimientoRef, { lastInteractionDate: fechaActual });
            setNuevaNota('');
            toast.success('Nota añadida.');
        } catch (error) {
            toast.error('No se pudo guardar la nota.');
            console.error(error);
        }
    };
    
    return (
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Bitácora de: {seguimiento.clienteName}</h3>
            <div className="mb-4">
                <textarea 
                    value={nuevaNota}
                    onChange={(e) => setNuevaNota(e.target.value)}
                    placeholder="Añadir nueva nota de seguimiento..."
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                    rows="3"
                ></textarea>
                <button onClick={handleAgregarNota} className="mt-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700">Guardar Nota</button>
            </div>
            <div className="flex-grow overflow-y-auto border-t pt-4">
                {cargandoBitacora ? <p>Cargando notas...</p> : 
                    bitacora.length > 0 ? (
                        bitacora.map(nota => (
                            <div key={nota.id} className="border-b py-2">
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{nota.texto}</p>
                                <p className="text-xs text-gray-500 mt-1">{nota.fecha?.toDate().toLocaleString('es-ES')}</p>
                            </div>
                        ))
                    ) : <p className="text-gray-500">No hay notas para este seguimiento.</p>
                }
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg self-end">Cerrar</button>
        </div>
    );
};

function SeguimientoView() {
    const [vistaActual, setVistaActual] = useState('enProceso');
    const [seguimientos, setSeguimientos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [terminoBusqueda, setTerminoBusqueda] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [seguimientoSeleccionado, setSeguimientoSeleccionado] = useState(null);

    useEffect(() => {
        const depurarYMigrar = async () => {
            const yaProcesado = localStorage.getItem('seguimientosDepuradosYMigrados_v1');
            if (yaProcesado) return;
            const depuracionToastId = toast.loading('Depurando datos duplicados...');
            try {
                const seguimientosRef = collection(db, 'seguimientosEnProceso');
                const snapshot = await getDocs(seguimientosRef);
                const vistos = new Map();
                const batch = writeBatch(db);
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (vistos.has(data.clienteId)) {
                        batch.delete(doc.ref);
                    } else {
                        vistos.set(data.clienteId, true);
                    }
                });
                await batch.commit();
                toast.success('Depuración completada.', { id: depuracionToastId });
            } catch (error) {
                console.error("Error en la depuración:", error);
                toast.error('Error durante la depuración.', { id: depuracionToastId });
            }
            const migracionToastId = toast.loading('Verificando datos...');
            try {
                const clientesSnapshot = await getDocs(collection(db, 'clientes'));
                const batch = writeBatch(db);
                for (const clienteDoc of clientesSnapshot.docs) {
                    const clienteData = clienteDoc.data();
                    const q = query(collection(db, 'seguimientosEnProceso'), where('clienteId', '==', clienteDoc.id));
                    const existente = await getDocs(q);
                    if (existente.empty) {
                        const bitacoraRef = collection(db, 'clientes', clienteDoc.id, 'bitacora_seguimiento');
                        const lastNoteQuery = query(bitacoraRef, orderBy('fecha', 'desc'), limit(1));
                        const lastNoteSnapshot = await getDocs(lastNoteQuery);
                        let lastInteractionDate = null;
                        if (!lastNoteSnapshot.empty) lastInteractionDate = lastNoteSnapshot.docs[0].data().fecha;
                        const nuevoSeguimiento = {
                            clienteId: clienteDoc.id,
                            clienteName: clienteData.name || 'Sin Nombre',
                            clienteEmail: clienteData.email || 'Sin Email',
                            pipelineStatus: clienteData.pipelineStatus || 'primer_contacto',
                            createdAt: clienteData.createdAt || serverTimestamp(),
                            lastInteractionDate: lastInteractionDate
                        };
                        const coleccionDestino = nuevoSeguimiento.pipelineStatus === 'proyecto_culminado' ? 'seguimientosCulminados' : 'seguimientosEnProceso';
                        batch.set(doc(collection(db, coleccionDestino)), nuevoSeguimiento);
                    }
                }
                await batch.commit();
                localStorage.setItem('seguimientosDepuradosYMigrados_v1', 'true');
                toast.success('Verificación completada.', { id: migracionToastId });
            } catch (error) {
                console.error("Error en la migración:", error);
                toast.error('Error durante la verificación.', { id: migracionToastId });
            }
        };
        depurarYMigrar();
    }, []);
    
    useEffect(() => {
        let coleccion = '';
        if (vistaActual === 'enProceso') coleccion = 'seguimientosEnProceso';
        else if (vistaActual === 'culminado') coleccion = 'seguimientosCulminados';
        else if (vistaActual === 'papelera') coleccion = 'seguimientosPapelera';
        if (!coleccion) return;
        setCargando(true);
        const q = query(collection(db, coleccion), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSeguimientos(data);
            setCargando(false);
        });
        return () => unsubscribe();
    }, [vistaActual]);

    const moveDocument = async (seguimientoId, origen, destino) => {
        const origenRef = doc(db, origen, seguimientoId);
        const docSnap = await getDoc(origenRef);
        if (docSnap.exists()) {
            const dataToMove = docSnap.data();
            await setDoc(doc(db, destino, seguimientoId), dataToMove);
            await deleteDoc(origenRef);
            return true;
        }
        return false;
    };

    const handleStatusChange = async (seguimiento, nuevoStatus) => {
        let origen;
        switch(vistaActual) {
            case 'enProceso': origen = 'seguimientosEnProceso'; break;
            case 'culminado': origen = 'seguimientosCulminados'; break;
            case 'papelera': origen = 'seguimientosPapelera'; break;
            default: return;
        }
        if (nuevoStatus === 'presupuesto_aprobado') {
            if (window.confirm(`¿Quieres aprobar este seguimiento y crear un nuevo proyecto para "${seguimiento.clienteName}"?`)) {
                const crearProyectoPromise = async () => {
                    const nuevoProyectoRef = await addDoc(collection(db, "proyectosEnTransito"), {
                        nombreProyecto: `Proyecto - ${seguimiento.clienteName}`,
                        clienteId: seguimiento.clienteId,
                        nombreCliente: seguimiento.clienteName,
                        estado: 'Aprobado',
                        fecha: serverTimestamp(),
                        departamento: 'Pendiente',
                        vendedor: 'Asignado desde Seguimiento',
                    });
                    const bitacoraAntiguaRef = collection(db, 'clientes', seguimiento.clienteId, 'bitacora_seguimiento');
                    const notasSnapshot = await getDocs(query(bitacoraAntiguaRef, orderBy('fecha', 'asc')));
                    if (!notasSnapshot.empty) {
                        const batch = writeBatch(db);
                        notasSnapshot.forEach(notaDoc => {
                            const notaData = notaDoc.data();
                            const historialNuevoRef = doc(collection(db, 'proyectosEnTransito', nuevoProyectoRef.id, 'historial'));
                            batch.set(historialNuevoRef, { ...notaData, estado: 'activo' });
                        });
                        await batch.commit();
                    }
                    await moveDocument(seguimiento.id, origen, 'seguimientosCulminados');
                };
                await toast.promise(crearProyectoPromise(), {
                    loading: 'Creando proyecto y transfiriendo historial...',
                    success: '¡Proyecto creado y seguimiento culminado!',
                    error: 'Error al crear el proyecto.'
                });
            }
            return;
        }
        if (nuevoStatus === 'proyecto_culminado') {
             if (window.confirm("¿Marcar este seguimiento como culminado?")) {
                await toast.promise(moveDocument(seguimiento.id, origen, 'seguimientosCulminados'), { 
                    loading: 'Culminando...', success: '¡Seguimiento culminado!', error: 'Error al culminar.' 
                });
            }
        } else {
            const docRef = doc(db, origen, seguimiento.id);
            await toast.promise(updateDoc(docRef, { pipelineStatus: nuevoStatus }), { 
                loading: 'Actualizando estado...', success: '¡Estado actualizado!', error: 'Error al actualizar.'
            });
        }
    };
    
    const handleDelete = async (seguimientoId) => {
         if (window.confirm("¿Mover a la papelera?")) {
            let origen = vistaActual === 'enProceso' ? 'seguimientosEnProceso' : 'seguimientosCulminados';
            await toast.promise(moveDocument(seguimientoId, origen, 'seguimientosPapelera'), { 
                loading: 'Moviendo a papelera...', success: 'Movido a papelera.', error: 'Error al mover.'
            });
        }
    };

    const handleRestore = async (seguimientoId) => {
        if (window.confirm("¿Quieres restaurar este seguimiento?")) {
            await toast.promise(moveDocument(seguimientoId, 'seguimientosPapelera', 'seguimientosEnProceso'), {
                loading: 'Restaurando seguimiento...',
                success: '¡Seguimiento restaurado con éxito!',
                error: 'Error al restaurar el seguimiento.'
            });
        }
    };

    // ==================================================================
    // ## CAMBIO 1: NUEVA FUNCIÓN PARA BORRADO PERMANENTE ##
    // Esta función elimina el documento directamente de la papelera.
    // ==================================================================
    const handlePermanentDelete = async (seguimientoId) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este seguimiento PARA SIEMPRE? Esta acción no se puede deshacer.")) {
            const docRef = doc(db, 'seguimientosPapelera', seguimientoId);
            await toast.promise(deleteDoc(docRef), {
                loading: 'Eliminando permanentemente...',
                success: '¡Seguimiento eliminado para siempre!',
                error: 'Error al eliminar el seguimiento.'
            });
        }
    };

    const sincronizarClientes = async () => {
        const promise = async () => {
            const clientesSnapshot = await getDocs(collection(db, 'clientes'));
            const seguimientosSnapshot = await getDocs(collection(db, 'seguimientosEnProceso'));
            const culminadosSnapshot = await getDocs(collection(db, 'seguimientosCulminados'));
            const papeleraSnapshot = await getDocs(collection(db, 'seguimientosPapelera'));
            const clienteIdsConSeguimiento = new Set([
                ...seguimientosSnapshot.docs.map(doc => doc.data().clienteId),
                ...culminadosSnapshot.docs.map(doc => doc.data().clienteId),
                ...papeleraSnapshot.docs.map(doc => doc.data().clienteId),
            ]);
            const batch = writeBatch(db);
            let nuevosSeguimientos = 0;
            clientesSnapshot.docs.forEach(clienteDoc => {
                if (!clienteIdsConSeguimiento.has(clienteDoc.id)) {
                    const clienteData = clienteDoc.data();
                    const nuevoSeguimientoRef = doc(collection(db, 'seguimientosEnProceso'));
                    batch.set(nuevoSeguimientoRef, {
                        clienteId: clienteDoc.id,
                        clienteName: clienteData.name || 'Sin Nombre',
                        clienteEmail: clienteData.email || 'Sin Email',
                        pipelineStatus: 'primer_contacto',
                        createdAt: clienteData.createdAt || serverTimestamp(),
                        lastInteractionDate: null
                    });
                    nuevosSeguimientos++;
                }
            });
            if (nuevosSeguimientos > 0) {
                await batch.commit();
                return `¡${nuevosSeguimientos} nuevo(s) seguimiento(s) creado(s)!`;
            } else {
                return '¡Todo está al día! No hay clientes nuevos por añadir.';
            }
        };
        toast.promise(promise(), {
            loading: 'Buscando clientes nuevos...',
            success: (message) => message,
            error: 'Error al sincronizar los clientes.'
        });
    };

    const seguimientosFiltrados = useMemo(() => {
        if (!terminoBusqueda) return seguimientos;
        const lowerCaseSearch = terminoBusqueda.toLowerCase();
        return seguimientos.filter(s => 
            (s.clienteName || '').toLowerCase().includes(lowerCaseSearch) ||
            (s.clienteEmail || '').toLowerCase().includes(lowerCaseSearch)
        );
    }, [seguimientos, terminoBusqueda]);
    
    const openModalConSeguimiento = (seguimiento) => {
        setSeguimientoSeleccionado(seguimiento);
        setModalOpen(true);
    };

    const coleccionActual = vistaActual === 'enProceso' ? 'seguimientosEnProceso' : 
                          vistaActual === 'culminado' ? 'seguimientosCulminados' : 'seguimientosPapelera';

    return (
        <div className="h-full flex flex-col p-4 md:p-6 bg-gray-100 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex-shrink-0">Seguimiento</h1>

            <div className="flex mb-4 border-b flex-shrink-0">
                <button onClick={() => setVistaActual('enProceso')} className={`py-2 px-4 font-semibold ${vistaActual === 'enProceso' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-500'}`}>En Proceso</button>
                <button onClick={() => setVistaActual('culminado')} className={`py-2 px-4 font-semibold ${vistaActual === 'culminado' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-500'}`}>Culminados</button>
                <button onClick={() => setVistaActual('papelera')} className={`py-2 px-4 font-semibold ${vistaActual === 'papelera' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-500'}`}>Papelera</button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <input
                    type="text"
                    placeholder="🔍 Buscar por nombre o email..."
                    value={terminoBusqueda}
                    onChange={(e) => setTerminoBusqueda(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                    onClick={sincronizarClientes}
                    className="px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 whitespace-nowrap"
                >
                    Sincronizar Nuevos Clientes
                </button>
            </div>
            
            <div className="flex-grow overflow-y-auto">
                {cargando ? ( <div className="text-center py-12 text-gray-500">Cargando seguimientos...</div> ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {seguimientosFiltrados.length > 0 ? (
                            seguimientosFiltrados.map(seguimiento => (
                                <div key={seguimiento.id} className="bg-white p-4 border rounded-lg shadow-sm hover:shadow-md cursor-pointer flex flex-col min-w-0" onClick={() => openModalConSeguimiento(seguimiento)}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-semibold text-gray-900 truncate">{seguimiento.clienteName}</p>
                                            <p className="text-sm text-gray-500 truncate">{seguimiento.clienteEmail}</p>
                                        </div>
                                        <div className="flex items-center gap-4 flex-shrink-0">
                                            <AlertaIndicator status={getAlertaStatus(seguimiento.lastInteractionDate)} />
                                            {/* ================================================================== */}
                                            {/* ## CAMBIO 2: AÑADIMOS EL BOTÓN DE BORRADO PERMANENTE ## */}
                                            {/* En la papelera, ahora se muestran ambos botones. */}
                                            {/* ================================================================== */}
                                            {vistaActual === 'papelera' ? (
                                                <>
                                                    <button onClick={(e) => { e.stopPropagation(); handleRestore(seguimiento.id); }} className="text-gray-400 hover:text-green-500" title="Restaurar">♻️</button>
                                                    <button onClick={(e) => { e.stopPropagation(); handlePermanentDelete(seguimiento.id); }} className="text-gray-400 hover:text-red-500" title="Eliminar para siempre">🗑️</button>
                                                </>
                                            ) : (
                                                <button onClick={(e) => { e.stopPropagation(); handleDelete(seguimiento.id); }} className="text-gray-400 hover:text-red-500" title="Mover a papelera">🗑️</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t flex-grow flex items-center">
                                        <PipelineTracker 
                                            currentStatus={seguimiento.pipelineStatus} 
                                            onStatusChange={(nuevoStatus) => handleStatusChange(seguimiento, nuevoStatus)}
                                            editable={vistaActual === 'enProceso'}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12 text-gray-500 col-span-1 md:col-span-2 xl:col-span-3">No hay seguimientos en esta vista.</div>
                        )}
                    </div>
                )}
            </div>
            
            {modalOpen && (
                <Modal 
                    isOpen={modalOpen} 
                    onRequestClose={() => setModalOpen(false)}
                    style={{ overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 }, content: { background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}}
                    ariaHideApp={false}
                >
                    <BitacoraModal seguimiento={seguimientoSeleccionado} onClose={() => setModalOpen(false)} coleccionOrigen={coleccionActual} />
                </Modal>
            )}
        </div>
    );
}

export default SeguimientoView;